import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Coins, TrendingUp, Loader2, RefreshCw, ChevronRight, Lock, Unlock, Gift, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Staking = () => {
  const { t } = useTranslation();
  const [pools, setPools] = useState([]);
  const [positions, setPositions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [staking, setStaking] = useState(false);
  const [selectedPool, setSelectedPool] = useState(null);
  const [stakeAmount, setStakeAmount] = useState('');

  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [poolsRes, positionsRes, summaryRes] = await Promise.all([
        axios.get(`${API}/staking/pools`),
        axios.get(`${API}/staking/positions`, { headers: getAuthHeader() }).catch(() => ({ data: { positions: [] } })),
        axios.get(`${API}/staking/summary`, { headers: getAuthHeader() }).catch(() => ({ data: {} }))
      ]);

      setPools(poolsRes.data.pools || []);
      setPositions(positionsRes.data.positions || []);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Error fetching staking data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStake = async () => {
    if (!selectedPool || !stakeAmount) {
      toast.error('Please select a pool and enter amount');
      return;
    }

    const amount = parseFloat(stakeAmount);
    if (amount < selectedPool.min_stake) {
      toast.error(`Minimum stake is ${selectedPool.min_stake} ${selectedPool.token}`);
      return;
    }

    setStaking(true);
    try {
      const response = await axios.post(
        `${API}/staking/stake`,
        { pool_id: selectedPool.id, amount },
        { headers: getAuthHeader() }
      );

      if (response.data.success) {
        toast.success(`Successfully staked ${amount} ${selectedPool.token}!`);
        setSelectedPool(null);
        setStakeAmount('');
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Staking failed');
    } finally {
      setStaking(false);
    }
  };

  const handleUnstake = async (stakeId) => {
    try {
      const response = await axios.post(
        `${API}/staking/unstake`,
        { stake_id: stakeId },
        { headers: getAuthHeader() }
      );

      if (response.data.success) {
        toast.success(`Unstaked ${response.data.amount} + ${response.data.rewards.toFixed(6)} rewards`);
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Unstake failed');
    }
  };

  const handleClaimRewards = async (stakeId) => {
    try {
      const response = await axios.post(
        `${API}/staking/claim`,
        { stake_id: stakeId },
        { headers: getAuthHeader() }
      );

      if (response.data.success) {
        toast.success(`Claimed ${response.data.rewards_claimed.toFixed(6)} ${response.data.token}!`);
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Claim failed');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div data-testid="staking-page" className="min-h-screen py-4 px-3 md:px-4">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-white mb-0.5">{t('staking.title')}</h1>
            <p className="text-white/60 text-xs">{t('staking.subtitle')}</p>
          </div>
          <button
            onClick={fetchData}
            className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
          >
            <RefreshCw className="w-4 h-4 text-white/60" />
          </button>
        </div>

        {/* Staking Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
          <div className="bg-black/40 backdrop-blur-xl border border-white/10 rounded-lg p-3">
            <div className="text-xs text-white/60 mb-0.5">{t('staking.totalStaked')}</div>
            <div className="text-lg font-mono font-bold text-white">
              ${summary?.total_staked?.toLocaleString() || '0.00'}
            </div>
          </div>
          <div className="bg-black/40 backdrop-blur-xl border border-white/10 rounded-lg p-3">
            <div className="text-xs text-white/60 mb-0.5">{t('staking.rewardsEarned')}</div>
            <div className="text-lg font-mono font-bold text-green-500">
              ${summary?.total_rewards?.toFixed(4) || '0.0000'}
            </div>
          </div>
          <div className="bg-black/40 backdrop-blur-xl border border-white/10 rounded-lg p-3">
            <div className="text-xs text-white/60 mb-0.5">{t('staking.avgAPY')}</div>
            <div className="text-lg font-mono font-bold text-white">
              {summary?.avg_apy?.toFixed(2) || '0.00'}%
            </div>
          </div>
          <div className="bg-black/40 backdrop-blur-xl border border-white/10 rounded-lg p-3">
            <div className="text-xs text-white/60 mb-0.5">Active Positions</div>
            <div className="text-lg font-mono font-bold text-white">
              {summary?.active_positions || 0}
            </div>
          </div>
        </div>

        {/* Active Positions */}
        {positions.length > 0 && (
          <div className="mb-4">
            <h2 className="text-base font-semibold text-white mb-2 flex items-center gap-2">
              <Wallet className="w-4 h-4 text-primary" />
              Your Positions
            </h2>
            <div className="space-y-2">
              {positions.map((position) => (
                <div
                  key={position.id}
                  className="bg-black/40 backdrop-blur-xl border border-white/10 rounded-lg p-3 flex items-center justify-between"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-primary/20 rounded-lg flex items-center justify-center">
                      <Coins className="w-4 h-4 text-primary" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-white">{position.pool_name}</div>
                      <div className="text-xs text-white/60">
                        {position.amount} {position.token} • {position.apy}% APY
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-right mr-2">
                      <div className="text-sm font-mono text-green-400">
                        +{position.current_rewards?.toFixed(6)} {position.reward_token}
                      </div>
                      <div className="text-xs text-white/50">Rewards</div>
                    </div>
                    <button
                      onClick={() => handleClaimRewards(position.id)}
                      className="px-2 py-1 bg-green-500/20 hover:bg-green-500/30 text-green-400 text-xs rounded-lg transition-colors"
                    >
                      <Gift className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => handleUnstake(position.id)}
                      className="px-2 py-1 bg-red-500/20 hover:bg-red-500/30 text-red-400 text-xs rounded-lg transition-colors"
                    >
                      <Unlock className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Staking Pools */}
        <h2 className="text-base font-semibold text-white mb-2 flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-green-500" />
          Available Pools
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
          {pools.map((pool) => (
            <div
              key={pool.id}
              data-testid={`staking-pool-${pool.id}`}
              onClick={() => setSelectedPool(selectedPool?.id === pool.id ? null : pool)}
              className={`bg-black/40 backdrop-blur-xl border rounded-lg p-3 cursor-pointer transition-all duration-300 ${
                selectedPool?.id === pool.id
                  ? 'border-primary bg-primary/10'
                  : 'border-white/10 hover:border-white/20 hover:bg-white/5'
              }`}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="w-8 h-8 bg-primary/20 rounded-lg flex items-center justify-center">
                  <Coins className="w-4 h-4 text-primary" />
                </div>
                <div className="flex items-center gap-1 bg-green-500/20 border border-green-500/30 px-2 py-0.5 rounded-full">
                  <TrendingUp className="w-2.5 h-2.5 text-green-500" />
                  <span className="text-xs font-mono text-green-500">{pool.apy}% APY</span>
                </div>
              </div>

              <h3 className="text-sm font-semibold text-white mb-0.5">{pool.name}</h3>
              <p className="text-white/60 text-xs mb-2">{pool.protocol} • {pool.type?.replace('_', ' ')}</p>

              <div className="space-y-1 mb-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-white/60">TVL</span>
                  <span className="text-xs font-mono text-white">{pool.tvl}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-white/60">Min Stake</span>
                  <span className="text-xs font-mono text-white">{pool.min_stake} {pool.token}</span>
                </div>
              </div>

              {selectedPool?.id === pool.id && (
                <div className="mt-2 pt-2 border-t border-white/10">
                  <div className="flex gap-2">
                    <input
                      type="number"
                      value={stakeAmount}
                      onChange={(e) => setStakeAmount(e.target.value)}
                      placeholder={`Min ${pool.min_stake}`}
                      onClick={(e) => e.stopPropagation()}
                      className="flex-1 bg-black/60 border border-white/20 rounded-lg px-2 py-1.5 text-xs text-white font-mono focus:border-primary focus:outline-none"
                    />
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleStake();
                      }}
                      disabled={staking}
                      className="bg-primary hover:bg-primary/90 text-white text-xs font-medium px-3 py-1.5 rounded-lg transition-all disabled:opacity-50"
                    >
                      {staking ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Stake'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Info Footer */}
        <div className="mt-4 bg-black/40 backdrop-blur-xl border border-white/10 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <Lock className="w-4 h-4 text-white/40 mt-0.5 flex-shrink-0" />
            <div className="text-xs text-white/50">
              <p className="mb-1"><strong className="text-white/70">Real Blockchain Staking:</strong> APY rates are fetched live from Lido, Polygon, and other staking protocols.</p>
              <p>Rewards accrue in real-time and can be claimed or compounded at any time.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Staking;
